import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';

import {
  OptInAddressData,
  OptInPersonalData,
  Pharmaceutical,
  PharmaceuticalCategory,
} from 'features/free-samples/services/types';
import { RequestStatus } from 'shared/constants/State';

export interface OptInConfirmationState {
  status?: RequestStatus;
  isOptinConfirmationModalOpen: boolean;
  isPharmasOffCanvasOpen: boolean;
  isSuccessModalOpen: boolean;
  isAddressModalOpen: boolean;
  isCategoriesOffCanvasOpen: boolean;
  availablePharmas?: {
    status?: RequestStatus;
    pharmas?: Pharmaceutical[];
  };
  selectedPharmas?: Pharmaceutical[];
  intercomEvents?: {
    status?: RequestStatus;
    event?: string;
  };
  optInPersonalData?: OptInPersonalData;
  optInAddressData?: OptInAddressData;
  availableCategories?: {
    status?: RequestStatus;
    categories?: PharmaceuticalCategory[];
  };
  selectedCategories?: PharmaceuticalCategory[];
}

export const initialState: OptInConfirmationState = {
  status: RequestStatus.Pending,
  isOptinConfirmationModalOpen: false,
  isPharmasOffCanvasOpen: false,
  isSuccessModalOpen: false,
  isAddressModalOpen: false,
  isCategoriesOffCanvasOpen: false,
};

export const nameStore = '@webapp/opt-in-confirmation';

export const fetchPharmasData = createAction(`${nameStore}/fetchPharmasData`);

export const fetchCategoriesData = createAction(
  `${nameStore}/fetchCategoriesData`,
);

export const setOptInConfirmation = createAction(
  `${nameStore}/setOptInConfirmation`,
);

export const sendIntercomEvent = createAction<string>(
  `${nameStore}/sendIntercomEvent`,
);

export const fetchClinicAddress = createAction<number>(
  `${nameStore}/fetchClinicAddress`,
);

const OptInConfirmationSlice = createSlice({
  name: nameStore,
  initialState,
  reducers: {
    submitOptInConfirmationSuccess: (state): OptInConfirmationState => ({
      ...state,
      status: RequestStatus.Success,
    }),
    submitOptInConfirmationFailure: (state): OptInConfirmationState => ({
      ...state,
      status: RequestStatus.Error,
    }),
    openConfirmationModal: (state): OptInConfirmationState => ({
      ...state,
      isOptinConfirmationModalOpen: true,
    }),
    closeConfirmationModal: (state): OptInConfirmationState => ({
      ...state,
      isOptinConfirmationModalOpen: false,
    }),
    goBackToConfirmationModal: (state): OptInConfirmationState => ({
      ...state,
      isOptinConfirmationModalOpen: true,
      isAddressModalOpen: false,
    }),
    openAddressModal: (state): OptInConfirmationState => ({
      ...state,
      isOptinConfirmationModalOpen: false,
      isAddressModalOpen: true,
    }),
    closeAddressModal: (state): OptInConfirmationState => ({
      ...state,
      isAddressModalOpen: false,
    }),
    goBackToAddressModal: (state): OptInConfirmationState => ({
      ...state,
      isPharmasOffCanvasOpen: false,
      isCategoriesOffCanvasOpen: false,
      isAddressModalOpen: true,
    }),
    openPharmasOffCanvas: (state): OptInConfirmationState => ({
      ...state,
      isPharmasOffCanvasOpen: true,
      isAddressModalOpen: false,
    }),
    closePharmasOffCanvas: (state): OptInConfirmationState => ({
      ...state,
      isPharmasOffCanvasOpen: false,
    }),
    fetchPharmasStart: (state): OptInConfirmationState => ({
      ...state,
      availablePharmas: {
        ...state.availablePharmas,
        status: RequestStatus.Pending,
      },
    }),
    fetchPharmasSuccess: (
      state,
      action: PayloadAction<Pharmaceutical[]>,
    ): OptInConfirmationState => ({
      ...state,
      availablePharmas: {
        status: RequestStatus.Success,
        pharmas: action.payload,
      },
    }),
    fetchPharmasFailure: (state): OptInConfirmationState => ({
      ...state,
      availablePharmas: {
        ...state.availablePharmas,
        status: RequestStatus.Error,
      },
    }),
    setSelectedPharmas: (state, action: PayloadAction<Pharmaceutical[]>) => ({
      ...state,
      selectedPharmas: action.payload,
      isPharmasOffCanvasOpen: false,
      isAddressModalOpen: false,
    }),
    goBackToPharmasModal: (state) => ({
      ...state,
      isCategoriesOffCanvasOpen: false,
      isPharmasOffCanvasOpen: true,
    }),
    openOptinCategoriesOffCanvas: (state): OptInConfirmationState => ({
      ...state,
      isCategoriesOffCanvasOpen: true,
      isPharmasOffCanvasOpen: false,
    }),
    closeOptinCategoriesOffCanvas: (state): OptInConfirmationState => ({
      ...state,
      isCategoriesOffCanvasOpen: false,
    }),
    fetchCategoriesStart: (state): OptInConfirmationState => ({
      ...state,
      availableCategories: {
        ...state.availableCategories,
        status: RequestStatus.Pending,
      },
    }),
    fetchCategoriesSuccess: (
      state,
      action: PayloadAction<PharmaceuticalCategory[]>,
    ): OptInConfirmationState => ({
      ...state,
      availableCategories: {
        status: RequestStatus.Success,
        categories: action.payload,
      },
    }),
    fetchCategoriesFailure: (state): OptInConfirmationState => ({
      ...state,
      availableCategories: {
        ...state.availableCategories,
        status: RequestStatus.Error,
      },
    }),
    setSelectedCategories: (
      state,
      action: PayloadAction<PharmaceuticalCategory[]>,
    ) => ({
      ...state,
      selectedCategories: action.payload,
      isPharmasOffCanvasOpen: false,
      isAddressModalOpen: false,
      isCategoriesOffCanvasOpen: false,
    }),
    openSuccessModal: (state): OptInConfirmationState => ({
      ...state,
      isSuccessModalOpen: true,
    }),
    closeSuccessModal: (state): OptInConfirmationState => ({
      ...state,
      isSuccessModalOpen: false,
    }),
    sendIntercomEventSuccess: (state, action: PayloadAction<string>) => ({
      ...state,
      intercomEvents: {
        ...state.intercomEvents,
        status: RequestStatus.Success,
        event: action.payload,
      },
    }),
    sendIntercomEventFailure: (state): OptInConfirmationState => ({
      ...state,
      intercomEvents: {
        ...state.intercomEvents,
        status: RequestStatus.Error,
      },
    }),
    setOptInPersonalData: (
      state,
      action: PayloadAction<OptInPersonalData>,
    ) => ({
      ...state,
      optInPersonalData: action.payload,
      status: RequestStatus.Pending,
    }),
    setOptInAddressData: (state, action: PayloadAction<OptInAddressData>) => ({
      ...state,
      optInAddressData: action.payload,
      status: RequestStatus.Pending,
    }),
    clearOptInAddressData: (state) => ({
      ...state,
      optInAddressData: undefined,
    }),
    resetOptIn: () => ({
      ...initialState,
    }),
  },
});

export const {
  submitOptInConfirmationSuccess,
  submitOptInConfirmationFailure,
  openConfirmationModal,
  closeConfirmationModal,
  goBackToConfirmationModal,
  openAddressModal,
  closeAddressModal,
  openPharmasOffCanvas,
  closePharmasOffCanvas,
  goBackToAddressModal,
  goBackToPharmasModal,
  fetchPharmasStart,
  fetchPharmasSuccess,
  fetchPharmasFailure,
  setSelectedPharmas,
  openOptinCategoriesOffCanvas,
  closeOptinCategoriesOffCanvas,
  fetchCategoriesStart,
  fetchCategoriesSuccess,
  fetchCategoriesFailure,
  setSelectedCategories,
  openSuccessModal,
  closeSuccessModal,
  sendIntercomEventSuccess,
  sendIntercomEventFailure,
  setOptInPersonalData,
  setOptInAddressData,
  clearOptInAddressData,
  resetOptIn,
} = OptInConfirmationSlice.actions;

export default OptInConfirmationSlice.reducer;
